import React, { useEffect, useState, useRef, createElement } from 'react';
import { Ticket, X, User, Mail, Phone, Globe } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
declare global {
  interface Window {
    fbq: any;
  }
}
const COUNTRY_CODES = [{
  code: '+1',
  country: 'USA/Canada'
}, {
  code: '+52',
  country: 'México'
}, {
  code: '+57',
  country: 'Colombia'
}, {
  code: '+34',
  country: 'España'
}, {
  code: '+39',
  country: 'Italia'
}, {
  code: '+54',
  country: 'Argentina'
}, {
  code: '+56',
  country: 'Chile'
}, {
  code: '+51',
  country: 'Perú'
}, {
  code: '+593',
  country: 'Ecuador'
}, {
  code: '+58',
  country: 'Venezuela'
}, {
  code: '+502',
  country: 'Guatemala'
}, {
  code: '+503',
  country: 'El Salvador'
}, {
  code: '+504',
  country: 'Honduras'
}, {
  code: '+505',
  country: 'Nicaragua'
}, {
  code: '+506',
  country: 'Costa Rica'
}, {
  code: '+507',
  country: 'Panamá'
}, {
  code: '+591',
  country: 'Bolivia'
}, {
  code: '+595',
  country: 'Paraguay'
}, {
  code: '+598',
  country: 'Uruguay'
}, {
  code: '+1-809',
  country: 'Rep. Dominicana'
}];
export function LandingPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [areaCode, setAreaCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [fbclid, setFbclid] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [fbp, setFbp] = useState('');
  const [fbc, setFbc] = useState('');
  const [utmParams, setUtmParams] = useState<Record<string, string>>({});
  const [userAgent, setUserAgent] = useState('');
  const [pageReferrer, setPageReferrer] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');
  // Refs for direct DOM access
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const areaCodeRef = useRef<HTMLSelectElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Check if we're on the /meta-martes path (requires SMS consent)
  const isMetaMartesPath = location.pathname === '/meta-martes';
  // Check if we're on paths that require SMS consent
  const isMetaJuevesPath = location.pathname === '/meta-jueves' || location.pathname === '/meta-jueves-lp2' || location.pathname === '/meta-jueves-rr' || location.pathname === '/jon-test';
  const isOrganicJuevesPath = location.pathname === '/organic-jueves';
  // Combined check for SMS consent requirement
  const requiresSmsConsent = isMetaMartesPath || isMetaJuevesPath || isOrganicJuevesPath;
  // Check if we're on the /yt-jueves path
  const isYtJuevesPath = location.pathname === '/yt-jueves';
  // Check if we're on the /en-vivo or /bienvenidos path (live mode)
  const isLiveModePath = location.pathname === '/en-vivo' || location.pathname === '/bienvenidos';
  // Check if we're on the /en-vivo path (live mode)
  const isEnVivoPath = location.pathname === '/en-vivo';
  // Check if we're on the /bienvenidos path (live mode)
  const isBienvenidosPath = location.pathname === '/bienvenidos' || location.pathname === '/bienvenidos-m';
  // Function to get next Thursday date with formatted month
  const getNextThursday = () => {
    const now = new Date();
    // Convert to EST timezone
    const estTime = new Date(now.toLocaleString('en-US', {
      timeZone: 'America/New_York'
    }));
    const currentDay = estTime.getDay(); // 0 = Sunday, 4 = Thursday
    const currentHour = estTime.getHours();
    let daysUntilThursday = 0;
    if (currentDay === 4) {
      // It's Thursday
      if (currentHour >= 19) {
        // After 7pm EST, show next week's Thursday
        daysUntilThursday = 7;
      } else {
        // Before 7pm EST, show today
        daysUntilThursday = 0;
      }
    } else if (currentDay < 4) {
      // Before Thursday this week
      daysUntilThursday = 4 - currentDay;
    } else {
      // After Thursday (Friday, Saturday, Sunday)
      daysUntilThursday = 7 - currentDay + 4;
    }
    const nextThursday = new Date(estTime);
    nextThursday.setDate(estTime.getDate() + daysUntilThursday);
    // Get day and month
    const day = nextThursday.getDate();
    const month = nextThursday.toLocaleString('es-ES', {
      month: 'long'
    }).toUpperCase();
    return `${month} ${day}`;
  };
  // Function to get next Tuesday date with formatted month (for /meta-martes)
  const getNextTuesday = () => {
    const now = new Date();
    // Convert to EST timezone
    const estTime = new Date(now.toLocaleString('en-US', {
      timeZone: 'America/New_York'
    }));
    const currentDay = estTime.getDay(); // 0 = Sunday, 2 = Tuesday
    const currentHour = estTime.getHours();
    let daysUntilTuesday = 0;
    if (currentDay === 2) {
      // It's Tuesday
      if (currentHour >= 19) {
        // After 7pm EST, show next week's Tuesday
        daysUntilTuesday = 7;
      } else {
        // Before 7pm EST, show today
        daysUntilTuesday = 0;
      }
    } else if (currentDay < 2) {
      // Before Tuesday this week (Sunday=0, Monday=1)
      daysUntilTuesday = 2 - currentDay;
    } else {
      // After Tuesday (Wednesday=3, Thursday=4, Friday=5, Saturday=6)
      daysUntilTuesday = 7 - currentDay + 2;
    }
    const nextTuesday = new Date(estTime);
    nextTuesday.setDate(estTime.getDate() + daysUntilTuesday);
    // Get day and month
    const day = nextTuesday.getDate();
    const month = nextTuesday.toLocaleString('es-ES', {
      month: 'long'
    }).toUpperCase();
    return `${month} ${day}`;
  };
  // Webhook mapping based on route
  const getWebhookUrl = () => {
    const webhookMap: {
      [key: string]: string;
    } = {
      '/meta-martes': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/DPM6R3UGsunm5BAERsxj',
      '/meta-jueves': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/9e77fbea-dfb7-4800-9398-1f4c8701198d',
      '/meta-jueves-lp2': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/9e77fbea-dfb7-4800-9398-1f4c8701198d',
      '/meta-jueves-rr': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/9e77fbea-dfb7-4800-9398-1f4c8701198d',
      '/jon-test': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/9e77fbea-dfb7-4800-9398-1f4c8701198d',
      '/reregister': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/9e77fbea-dfb7-4800-9398-1f4c8701198d',
      '/news-brake': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/EWg1a06f5y2PHE4fsUxv',
      '/organic-martes': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/574d312b-45e4-4339-807b-ff590beea11d',
      '/organic-jueves': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/9e77fbea-dfb7-4800-9398-1f4c8701198d',
      '/yt-jueves': 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/e026861b-db91-4642-96ba-9fb9c82470e1'
    };
    return webhookMap[location.pathname] || webhookMap['/meta-martes'];
  };
  useEffect(() => {
    // === FULL TRACKING SUITE ===
    // 1. Capture full landing page URL (with all params)
    setLandingPageUrl(window.location.href);
    // 2. Capture referrer
    setPageReferrer(document.referrer || '');
    // 3. Capture user agent
    setUserAgent(navigator.userAgent || '');
    // 4. Extract ALL URL parameters (fbclid + UTMs + any custom params)
    const urlParams = new URLSearchParams(window.location.search);
    const fbclidParam = urlParams.get('fbclid');
    if (fbclidParam) {
      setFbclid(fbclidParam);
      // Build fbc from fbclid if no _fbc cookie exists
      const fbcCookie = document.cookie.split('; ').find(row => row.startsWith('_fbc='))?.split('=')[1];
      setFbc(fbcCookie || `fb.1.${Date.now()}.${fbclidParam}`);
    } else {
      // Try reading _fbc cookie even without fbclid in URL
      const fbcCookie = document.cookie.split('; ').find(row => row.startsWith('_fbc='))?.split('=')[1];
      if (fbcCookie) setFbc(fbcCookie);
    }
    // 5. Extract UTM parameters
    const utms: Record<string, string> = {};
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
    utmKeys.forEach(key => {
      const val = urlParams.get(key);
      if (val) utms[key] = val;
    });
    // Also capture any ad-platform specific params
    const adParams = ['gclid', 'gbraid', 'wbraid', 'ttclid', 'ScCid', 'msclkid'];
    adParams.forEach(key => {
      const val = urlParams.get(key);
      if (val) utms[key] = val;
    });
    setUtmParams(utms);
    // 6. Read _fbp cookie (Facebook Browser ID - set by Meta Pixel)
    // Needs a short delay to let Meta Pixel set the cookie first
    const readFbpCookie = () => {
      const fbpCookie = document.cookie.split('; ').find(row => row.startsWith('_fbp='))?.split('=')[1];
      if (fbpCookie) {
        setFbp(fbpCookie);
        console.log('fbp captured:', fbpCookie);
      }
    };
    // Read immediately in case it already exists
    readFbpCookie();
    // Also read after a delay to catch when Meta Pixel sets it
    const fbpTimer = setTimeout(readFbpCookie, 2000);
    // 7. Fetch user IP address
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
      if (data.ip) {
        setIpAddress(data.ip);
        console.log('IP address captured:', data.ip);
      }
    }).catch(err => {
      console.warn('Could not fetch IP address:', err);
    });
    // Log all tracking data
    console.log('=== TRACKING SUITE INIT ===');
    console.log('fbclid:', fbclidParam || 'none');
    console.log('UTMs:', utms);
    console.log('Referrer:', document.referrer);
    console.log('User Agent:', navigator.userAgent);
    console.log('Landing URL:', window.location.href);
    console.log('===========================');
    (function (f: any, b: any, e: any, v: any, n: any, t: any, s: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        b.head.appendChild(t);
      }
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', null, null, null);
    window.fbq('init', '754551913878347');
    window.fbq('track', 'PageView');
    return () => {
      clearTimeout(fbpTimer);
    };
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    // Get values from multiple sources for maximum reliability
    // Priority: Direct DOM ref > React state > FormData
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    // Get values directly from DOM refs (most reliable for autofill)
    const domName = nameInputRef.current?.value?.trim() || '';
    const domEmail = emailInputRef.current?.value?.trim() || '';
    const domPhone = phoneInputRef.current?.value?.replace(/\D/g, '').trim() || '';
    const domAreaCode = areaCodeRef.current?.value || '+1';
    // Use DOM values first, then React state, then FormData as fallbacks
    const finalName = domName || name.trim() || (formData.get('name') as string)?.trim() || '';
    const finalEmail = domEmail || email.trim() || (formData.get('email') as string)?.trim() || '';
    const finalPhone = domPhone || phone.trim() || (formData.get('phone') as string)?.replace(/\D/g, '').trim() || '';
    const finalAreaCode = domAreaCode || areaCode || formData.get('areaCode') as string || '+1';
    // Log all values for debugging
    console.log('Form submission values:', {
      domValues: {
        domName,
        domEmail,
        domPhone,
        domAreaCode
      },
      stateValues: {
        name,
        email,
        phone,
        areaCode
      },
      finalValues: {
        finalName,
        finalEmail,
        finalPhone,
        finalAreaCode
      }
    });
    // Strict validation - do not submit if any required field is empty
    if (!finalName || finalName.length < 2) {
      console.error('Name is missing or too short:', finalName);
      alert('Por favor ingresa tu nombre completo');
      return;
    }
    if (!finalEmail || !finalEmail.includes('@')) {
      console.error('Email is missing or invalid:', finalEmail);
      alert('Por favor ingresa un email válido');
      return;
    }
    if (!finalPhone || finalPhone.length < 7) {
      console.error('Phone is missing or too short:', finalPhone);
      alert('Por favor ingresa tu número de teléfono');
      return;
    }
    // Check SMS consent for paths that require it
    if (requiresSmsConsent && !smsConsent) {
      alert('Por favor marca la casilla de consentimiento de SMS para continuar.');
      return;
    }
    setIsSubmitting(true);
    try {
      const webhookUrl = getWebhookUrl();
      const payload = {
        // Standard field names
        name: finalName,
        email: finalEmail,
        phone: `${finalAreaCode}${finalPhone}`,
        // GoHighLevel specific field names
        first_name: finalName.split(' ')[0],
        last_name: finalName.split(' ').slice(1).join(' ') || finalName.split(' ')[0],
        full_name: finalName,
        // Phone variations for GHL compatibility
        phoneNumber: finalPhone,
        phone_number: finalPhone,
        fullPhone: `${finalAreaCode}${finalPhone}`,
        full_phone: `${finalAreaCode}${finalPhone}`,
        areaCode: finalAreaCode,
        area_code: finalAreaCode,
        // Additional contact info
        contact_name: finalName,
        contactName: finalName,
        // Source tracking
        source: location.pathname,
        page_path: location.pathname,
        // === FACEBOOK CONVERSIONS API DATA ===
        // Facebook Click ID
        fbclid: fbclid || '',
        // Facebook Click Cookie (fbc) - for CAPI matching
        fbc: fbc || '',
        // Facebook Browser ID (fbp) - for CAPI matching
        fbp: fbp || '',
        // IP Address - for CAPI matching
        ip_address: ipAddress || '',
        ipAddress: ipAddress || '',
        // User Agent - for CAPI matching
        user_agent: userAgent || '',
        userAgent: userAgent || '',
        // === UTM PARAMETERS ===
        utm_source: utmParams.utm_source || '',
        utm_medium: utmParams.utm_medium || '',
        utm_campaign: utmParams.utm_campaign || '',
        utm_content: utmParams.utm_content || '',
        utm_term: utmParams.utm_term || '',
        utm_id: utmParams.utm_id || '',
        // === ADDITIONAL AD PLATFORM IDs ===
        gclid: utmParams.gclid || '',
        gbraid: utmParams.gbraid || '',
        wbraid: utmParams.wbraid || '',
        ttclid: utmParams.ttclid || '',
        msclkid: utmParams.msclkid || '',
        // === EVENT METADATA ===
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: landingPageUrl || window.location.href,
        action_source: 'website',
        page_referrer: pageReferrer || '',
        landing_page: landingPageUrl || window.location.href
      };
      console.log('=== WEBHOOK DEBUG ===');
      console.log('Current pathname:', location.pathname);
      console.log('Webhook URL:', webhookUrl);
      console.log('Payload:', payload);
      console.log('====================');
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      console.log('Webhook response status:', response.status);
      console.log('Webhook response ok:', response.ok);
      if (!response.ok) {
        console.error('Webhook failed with status:', response.status);
        const responseText = await response.text();
        console.error('Response body:', responseText);
      }
      // Navigate to thank you page regardless (user experience)
      // but log if there was an issue
      navigate(isMetaMartesPath ? '/thank-you-m' : '/thank-you');
    } catch (error) {
      console.error('Error submitting form:', error);
      // Still navigate to thank you for better UX, but log the error
      navigate(isMetaMartesPath ? '/thank-you-m' : '/thank-you');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-black text-white font-sans overflow-x-hidden" data-id="element-1552">
      {/* Meta Pixel Noscript */}
      <noscript data-id="element-1553">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=754551913878347&ev=PageView&noscript=1" alt="" data-id="element-1554" />
      </noscript>

      {/* Background Image Layer */}
      <div className="absolute top-0 left-0 w-full h-[400px] md:h-[600px] z-0 opacity-60" data-id="element-1555">
        <img src="https://cdn.magicpatterns.com/uploads/owSR6X5Pw8KuRfkif7sGzk/6936d7f7169a421d1ba8a294.png" alt="Background Pattern" className="w-full h-full object-cover mask-image-gradient" style={{
        maskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)'
      }} data-id="element-1556" />
      </div>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-3 md:px-4 pt-4 md:pt-8 pb-12 md:pb-20 flex flex-col items-center text-center" data-id="element-1557">
        {isBienvenidosPath /* BIENVENIDOS PATH - Download Guide + WhatsApp */ ? <div className="flex flex-col items-center justify-center w-full py-8" data-id="element-1558">
            {/* Step 1: Download Guide */}
            <div className="w-full max-w-3xl mb-12" data-id="element-1559">
              <div className="bg-gradient-to-r from-[#00f7fc] to-[#00d7dc] rounded-full py-3 px-6 text-center mb-6 inline-block" data-id="element-1560">
                <h2 className="text-xl md:text-2xl font-black text-black tracking-tight" data-id="element-1561">
                  Paso 1: Descarga Tu Guía Gratis
                </h2>
              </div>

              <a href="https://drive.google.com/file/d/1UwCS-6hTXssQIqMUObPKvMnumSsTNm5u/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="block group" data-id="element-1562">
                <div className="relative" data-id="element-1563">
                  <img src="https://cdn.magicpatterns.com/uploads/rdPbQ6QLyf52zvdPSfVa7T/Banner_Secuencia_Correcta.png" alt="Descarga Tu Guía Gratis - Secuencia Correcta" className="w-full h-auto rounded-lg shadow-2xl group-hover:scale-[1.02] transition-all duration-300 cursor-pointer" style={{
                boxShadow: '0 0 40px rgba(0, 247, 252, 0.3), 0 0 80px rgba(0, 247, 252, 0.15)'
              }} data-id="element-1564" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20 rounded-lg" data-id="element-1565">
                    <div className="bg-[#00f7fc] text-black px-8 py-4 rounded-full font-black text-lg shadow-lg" data-id="element-1566">
                      DESCARGAR GUÍA
                    </div>
                  </div>
                </div>
              </a>
            </div>

            {/* Step 2: WhatsApp Group */}
            <div className="w-full max-w-xl" data-id="element-1567">
              <div className="bg-[#25D366] rounded-full py-3 px-6 text-center mb-6 inline-block" data-id="element-1568">
                <h2 className="text-xl md:text-2xl font-black text-white tracking-tight" data-id="element-1569">
                  Paso 2: No Te Olvides De Unirte Al Grupo De WhatsApp
                </h2>
              </div>

              <a href="https://chat.whatsapp.com/EIwl7s91Bzw4mcz9kdw94X" target="_blank" rel="noopener noreferrer" className="group relative w-full bg-gradient-to-b from-[#25D366] to-[#128C7E] hover:from-[#2EE374] hover:to-[#1BA06A] text-white rounded-full py-5 px-10 md:py-6 md:px-16 shadow-[0_0_40px_rgba(37,211,102,0.4)] transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-3" data-id="element-1570">
                <svg className="w-8 h-8 md:w-10 md:h-10" viewBox="0 0 24 24" fill="currentColor" data-id="element-1571">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-1572" />
                </svg>
                <span className="text-xl md:text-2xl lg:text-3xl font-black uppercase tracking-tight" data-id="element-1573">
                  UNIRME AL GRUPO
                </span>
              </a>
            </div>
          </div> : isEnVivoPath /* EN VIVO PATH - Simplified Live Mode */ ? <div className="flex flex-col items-center justify-center min-h-[60vh] w-full" data-id="element-1574">
            {/* Live Indicator */}
            <div className="flex items-center gap-3 mb-8" data-id="element-1575">
              <span className="relative flex h-4 w-4" data-id="element-1576">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" data-id="element-1577"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-red-600" data-id="element-1578"></span>
              </span>
              <span className="text-red-500 font-bold text-lg md:text-xl uppercase tracking-wider" data-id="element-1579">
                EN VIVO AHORA
              </span>
            </div>

            {/* Main Message */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black uppercase leading-tight mb-8 text-white" data-id="element-1580">
              Estamos En Vivo
            </h1>

            <p className="text-xl md:text-2xl lg:text-3xl text-gray-300 mb-12" data-id="element-1581">
              Únete A La Masterclass Acá
            </p>

            {/* CTA Button - Links to Zoom/Live */}
            <a href="https://www.youtube.com/watch?v=QwktM8Urxng" target="_blank" rel="noopener noreferrer" className="group relative bg-gradient-to-b from-[#FDB931] to-[#D68E15] hover:from-[#FFC54D] hover:to-[#E6A02E] text-black rounded-full py-5 px-10 md:py-6 md:px-16 shadow-[0_0_40px_rgba(253,185,49,0.4)] transition-all duration-300 transform hover:scale-[1.02]" data-id="element-1582">
              <div className="flex items-center gap-3" data-id="element-1583">
                <span className="relative flex h-3 w-3" data-id="element-1584">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black/30 opacity-75" data-id="element-1585"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-black/50" data-id="element-1586"></span>
                </span>
                <span className="text-xl md:text-2xl lg:text-3xl font-black uppercase tracking-tight" data-id="element-1587">
                  ENTRAR A LA MASTERCLASS
                </span>
              </div>

              {/* Shine effect */}
              <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none" data-id="element-1588">
                <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-[#00D9FF]/60 to-transparent skew-x-[-25deg] group-hover:animate-shine" data-id="element-1589" />
              </div>
            </a>
          </div> /* NORMAL PATHS - Full Landing Page */ : <>
            {/* Hero Image (Person + Logo) */}
            <div className="w-full max-w-[320px] md:max-w-[500px] lg:max-w-[600px] mb-1 md:mb-2 animate-fade-in-up" data-id="element-1590">
              <img src="https://cdn.magicpatterns.com/uploads/rZjvDAnMDLXJQX7she8uyT/Banner_48h_Cash_Machine_2.png" alt="Gabriel Beltran 48H AI Cash Machine Live" className="w-full h-auto drop-shadow-2xl" data-id="element-1591" />
            </div>

            {/* Subtitle */}
            <h2 className="text-sm md:text-lg lg:text-xl font-bold uppercase tracking-wide mb-3 md:mb-6 text-white leading-tight px-2" data-id="element-1592">
              {isYtJuevesPath ? <>
                  FORMACIÓN EDUCATIVA SOBRE INTELIGENCIA ARTIFICIAL Y PRODUCTOS
                  DIGITALES
                  <br className="hidden md:block" data-id="element-1593" />
                  Contenido explicado desde nivel introductorio
                </> : location.pathname === '/meta-jueves-lp2' ? <>
                  CÓMO EMPRENDEDORES Y PROFESIONALES ESTÁN USANDO IA PARA LANZAR
                  PRODUCTOS DIGITALES RENTABLES EN 48 HORAS
                  <br className="hidden md:block" data-id="element-1594" />
                  (Sin Contratar Agencias ni Programadores)
                </> : <>
                  GENERA INGRESOS CON INTELIGENCIA ARTIFICIAL
                  <br className="hidden md:block" data-id="element-1595" />
                  SIN EXPERIENCIA, SIN SEGUIDORES
                </>}
            </h2>

            {/* Date & Time */}
            <div className="mb-4 md:mb-8" data-id="element-1596">
              <p className="text-lg md:text-2xl lg:text-3xl font-bold tracking-wide text-[#FDB931]" style={{
            textShadow: '0 0 20px rgba(253, 185, 49, 0.3)'
          }} data-id="element-1597">
                {isMetaMartesPath ? `${getNextTuesday()} | 7PM EST (Hora Miami)` : `${getNextThursday()} | 7PM EST (Hora Miami)`}
              </p>
            </div>

            {/* Subtext */}
            <p className="text-gray-300 text-xs md:text-base lg:text-lg mb-6 md:mb-10 max-w-3xl px-2" data-id="element-1598">
              {isYtJuevesPath ? <>
                  Regístrate{' '}
                  <span className="font-bold text-white" data-id="element-1599">GRATIS</span> y accede
                  a la sesión educativa.
                </> : location.pathname === '/meta-jueves-lp2' ? <>
                  Esta clase es para personas que están listas para construir un
                  activo digital real. Si solo buscas información gratuita, este
                  no es tu lugar.
                </> : <>
                  No te quedes por fuera - Regístrate{' '}
                  <span className="font-bold text-white" data-id="element-1600">GRATIS</span> y empieza
                  hoy mismo tu camino para generar ingresos con IA.
                </>}
            </p>

            {/* CTA Button (Opens Modal) */}
            <div className="w-full max-w-2xl mb-8 md:mb-16" data-id="element-1601">
              <button type="button" onClick={() => setShowRegisterModal(true)} className="group relative w-full bg-gradient-to-b from-[#FDB931] to-[#D68E15] hover:from-[#FFC54D] hover:to-[#E6A02E] text-black rounded-full py-3 px-4 md:py-4 md:px-6 lg:py-6 lg:px-10 shadow-[0_0_40px_rgba(253,185,49,0.4)] transition-all duration-300 transform hover:scale-[1.02]" data-id="element-1602">
                <div className="flex flex-col items-center justify-center" data-id="element-1603">
                  <div className="flex items-center gap-2 md:gap-3 mb-0.5 md:mb-1" data-id="element-1604">
                    <Ticket className="w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 text-black fill-black/10" data-id="element-1605" />
                    <span className={`font-black uppercase tracking-tight ${location.pathname === '/meta-jueves-lp2' ? 'text-base md:text-xl lg:text-3xl' : 'text-lg md:text-2xl lg:text-4xl'}`} data-id="element-1606">
                      {location.pathname === '/meta-jueves-lp2' ? 'RESERVAR MI LUGAR EN LA MASTERCLASS' : 'ASEGURA TU CUPO GRATIS'}
                    </span>
                  </div>
                  <span className="text-xs md:text-sm lg:text-base font-medium opacity-90" data-id="element-1607">
                    {location.pathname === '/meta-jueves-lp2' ? 'Solo 100 cupos disponibles. Acceso limitado.' : 'Solo 100 Cupos. Una vez lleno, se cierra el registro.'}
                  </span>
                </div>

                {/* Shine effect */}
                <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none" data-id="element-1608">
                  <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-[#00D9FF]/60 to-transparent skew-x-[-25deg] group-hover:animate-shine" data-id="element-1609" />
                </div>
              </button>
            </div>

            {/* Free Gift Section */}
            <div className="w-full max-w-[90%] md:max-w-4xl animate-fade-in-up delay-200" data-id="element-1610">
              <img src="https://cdn.magicpatterns.com/uploads/2vWhRAK78YyzGd1wfFKwa7/heylloyd-image-1769473134106.png" alt="Regalo Gratis - Guía de la Secuencia Correcta" className="w-full h-auto drop-shadow-2xl hover:scale-[1.01] transition-transform duration-500" data-id="element-1611" />
            </div>
          </>}

        {/* Disclaimer - only on /meta-jueves-lp2 */}
        {location.pathname === '/meta-jueves-lp2' && <div className="mt-8 md:mt-12 max-w-3xl mx-auto px-4" data-id="element-1612">
            <p className="text-gray-500 text-[10px] md:text-xs text-center italic leading-relaxed" data-id="element-1613">
              Esto es para personas serias que quieren construir un negocio
              real, no un botón mágico de riqueza.
            </p>
          </div>}
      </main>

      {/* Footer */}
      <Footer data-id="element-1614" />

      {/* Registration Modal */}
      {showRegisterModal && <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowRegisterModal(false)} data-id="element-1615">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-md w-full p-6 md:p-8 relative shadow-[0_0_50px_rgba(253,185,49,0.15)]" onClick={e => e.stopPropagation()} data-id="element-1616">
            <button onClick={() => setShowRegisterModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors p-1" data-id="element-1617">
              <X className="w-6 h-6" data-id="element-1618" />
            </button>

            <div className="text-center mb-6" data-id="element-1619">
              <h2 className="text-2xl md:text-3xl font-black text-white mb-2 uppercase" data-id="element-1620">
                Completa Tu Registro
              </h2>
              <p className="text-gray-400 text-sm" data-id="element-1621">
                Ingresa tus datos para asegurar tu cupo gratuito
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1622">
              {/* Name Input */}
              <div className="space-y-1" data-id="element-1623">
                <label className="text-xs font-bold text-gray-400 uppercase ml-2" data-id="element-1624">
                  Nombre Completo
                </label>
                <div className="relative" data-id="element-1625">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1626">
                    <User className="h-5 w-5 text-gray-500" data-id="element-1627" />
                  </div>
                  <input ref={nameInputRef} type="text" name="name" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" required minLength={2} autoComplete="name" className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#FDB931] focus:bg-white/10 transition-all" data-id="element-1628" />
                </div>
              </div>

              {/* Email Input */}
              <div className="space-y-1" data-id="element-1629">
                <label className="text-xs font-bold text-gray-400 uppercase ml-2" data-id="element-1630">
                  Tu Mejor Email
                </label>
                <div className="relative" data-id="element-1631">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1632">
                    <Mail className="h-5 w-5 text-gray-500" data-id="element-1633" />
                  </div>
                  <input ref={emailInputRef} type="email" name="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ejemplo@correo.com" required autoComplete="email" className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#FDB931] focus:bg-white/10 transition-all" data-id="element-1634" />
                </div>
              </div>

              {/* Phone Input Group */}
              <div className="space-y-1" data-id="element-1635">
                <label className="text-xs font-bold text-gray-400 uppercase ml-2" data-id="element-1636">
                  WhatsApp / Teléfono
                </label>
                <div className="flex gap-2" data-id="element-1637">
                  {/* Area Code Selector */}
                  <div className="relative w-1/3" data-id="element-1638">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none" data-id="element-1639">
                      <Globe className="h-4 w-4 text-gray-500" data-id="element-1640" />
                    </div>
                    <select ref={areaCodeRef} name="areaCode" value={areaCode} onChange={e => setAreaCode(e.target.value)} className="w-full pl-8 pr-2 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#FDB931] focus:bg-white/10 transition-all appearance-none text-sm" data-id="element-1641">
                      {COUNTRY_CODES.map(country => <option key={country.code} value={country.code} className="bg-gray-900" data-id="element-1642">
                          {country.code} {country.country}
                        </option>)}
                    </select>
                  </div>

                  {/* Phone Number Input */}
                  <div className="relative w-2/3" data-id="element-1643">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1644">
                      <Phone className="h-5 w-5 text-gray-500" data-id="element-1645" />
                    </div>
                    <input ref={phoneInputRef} type="tel" name="phone" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="Número" required minLength={7} autoComplete="tel" className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#FDB931] focus:bg-white/10 transition-all" data-id="element-1646" />
                  </div>
                </div>
              </div>

              {/* SMS Consent Checkbox - Shown on paths that require SMS consent */}
              {requiresSmsConsent && <div className="space-y-2" data-id="element-1647">
                  <label className="flex items-start gap-3 cursor-pointer group" data-id="element-1648">
                    <div className="relative flex-shrink-0 mt-0.5" data-id="element-1649">
                      <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="sr-only peer" data-id="element-1650" />
                      <div className="w-5 h-5 border-2 border-gray-500 rounded bg-white/5 peer-checked:bg-[#FDB931] peer-checked:border-[#FDB931] transition-all flex items-center justify-center" data-id="element-1651">
                        {smsConsent && <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-1652">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-1653" />
                          </svg>}
                      </div>
                    </div>
                    <span className="text-[10px] leading-tight text-gray-400 group-hover:text-gray-300 transition-colors" data-id="element-1654">
                      Al marcar esta casilla, acepto recibir mensajes SMS/texto
                      recurrentes de True Marketer LLC al número móvil
                      proporcionado, incluyendo actualizaciones del programa,
                      información de capacitación y mensajes de
                      marketing/promocionales, enviados mediante un sistema de
                      marcación telefónica automática/tecnología automatizada.
                      La frecuencia de los mensajes varía. Pueden aplicar
                      tarifas de mensajes y datos. Responde STOP para cancelar,
                      HELP para ayuda. El consentimiento no es una condición de
                      compra. Ver{' '}
                      <Link to="/terms" className="text-[#FDB931] hover:text-[#FFC54D] underline" data-id="element-1655">
                        Términos
                      </Link>{' '}
                      y{' '}
                      <Link to="/privacy" className="text-[#FDB931] hover:text-[#FFC54D] underline" data-id="element-1656">
                        Política de Privacidad
                      </Link>
                      .
                    </span>
                  </label>
                </div>}

              {/* Submit Button */}
              <button type="submit" disabled={isSubmitting} className="w-full mt-6 bg-gradient-to-b from-[#FDB931] to-[#D68E15] hover:from-[#FFC54D] hover:to-[#E6A02E] disabled:opacity-50 disabled:cursor-not-allowed text-black font-black uppercase tracking-wide rounded-xl py-4 shadow-lg hover:shadow-[#FDB931]/20 transition-all duration-300 transform hover:scale-[1.02]" data-id="element-1657">
                {isSubmitting ? 'PROCESANDO...' : 'CONFIRMAR REGISTRO'}
              </button>

              <p className="text-center text-xs text-gray-500 mt-4" data-id="element-1658">
                Tus datos están 100% seguros y no compartiremos tu información.
              </p>
            </form>
          </div>
        </div>}

      {/* Terms Modal */}
      {showTerms && <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowTerms(false)} data-id="element-1659">
          <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 md:p-8 relative" onClick={e => e.stopPropagation()} data-id="element-1660">
            <button onClick={() => setShowTerms(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors" data-id="element-1661">
              <X className="w-6 h-6" data-id="element-1662" />
            </button>
            <h2 className="text-2xl md:text-3xl font-bold mb-6" data-id="element-1663">
              Términos y Condiciones
            </h2>
            <div className="text-gray-300 text-sm md:text-base space-y-4 leading-relaxed" data-id="element-1664">
              <p data-id="element-1665">
                Al registrarte y participar en el 48 Hrs AI Cash Machine
                Masterclass, aceptas los siguientes términos y condiciones:
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1666">
                1. Aceptación de Términos
              </h3>
              <p data-id="element-1667">
                Al acceder y utilizar este sitio web y registrarte para el
                evento, aceptas estar sujeto a estos términos y condiciones,
                todas las leyes y regulaciones aplicables.
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1668">
                2. Uso del Servicio
              </h3>
              <p data-id="element-1669">
                El contenido proporcionado en el masterclass es solo para fines
                educativos e informativos. No garantizamos resultados
                específicos de ingresos o éxito comercial.
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1670">
                3. Propiedad Intelectual
              </h3>
              <p data-id="element-1671">
                Todo el contenido, materiales, y recursos proporcionados son
                propiedad de 48 Hrs AI Cash Machine y están protegidos por
                derechos de autor. No está permitida la reproducción o
                distribución sin autorización expresa.
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1672">
                4. Limitación de Responsabilidad
              </h3>
              <p data-id="element-1673">
                48 Hrs AI Cash Machine no será responsable de ningún daño
                directo, indirecto, incidental o consecuente que resulte del uso
                o la imposibilidad de usar nuestros servicios.
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1674">
                5. Modificaciones
              </h3>
              <p data-id="element-1675">
                Nos reservamos el derecho de modificar estos términos en
                cualquier momento. El uso continuado del servicio después de
                cambios constituye la aceptación de los nuevos términos.
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1676">Contacto</h3>
              <p data-id="element-1677">
                Para preguntas sobre estos términos, contacta:{' '}
                <a href="mailto:support@truemarketer.com" className="text-[#00f7fc] hover:text-[#00d7dc]" data-id="element-1678">
                  support@truemarketer.com
                </a>
              </p>
            </div>
          </div>
        </div>}

      {/* Privacy Modal */}
      {showPrivacy && <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPrivacy(false)} data-id="element-1679">
          <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 md:p-8 relative" onClick={e => e.stopPropagation()} data-id="element-1680">
            <button onClick={() => setShowPrivacy(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors" data-id="element-1681">
              <X className="w-6 h-6" data-id="element-1682" />
            </button>
            <h2 className="text-2xl md:text-3xl font-bold mb-6" data-id="element-1683">
              Política de Privacidad
            </h2>
            <div className="text-gray-300 text-sm md:text-base space-y-4 leading-relaxed" data-id="element-1684">
              <p data-id="element-1685">
                En 48 Hrs AI Cash Machine, nos comprometemos a proteger tu
                privacidad. Esta política describe cómo recopilamos, usamos y
                protegemos tu información personal.
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1686">
                1. Información que Recopilamos
              </h3>
              <p data-id="element-1687">
                Recopilamos información que nos proporcionas directamente,
                incluyendo:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1688">
                <li data-id="element-1689">Nombre y dirección de correo electrónico</li>
                <li data-id="element-1690">Información de registro del evento</li>
                <li data-id="element-1691">Comunicaciones que mantienes con nosotros</li>
              </ul>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1692">
                2. Uso de la Información
              </h3>
              <p data-id="element-1693">Utilizamos tu información para:</p>
              <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1694">
                <li data-id="element-1695">
                  Proporcionar acceso al masterclass y materiales relacionados
                </li>
                <li data-id="element-1696">Enviar comunicaciones sobre el evento</li>
                <li data-id="element-1697">Mejorar nuestros servicios y contenido</li>
                <li data-id="element-1698">Cumplir con obligaciones legales</li>
              </ul>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1699">
                3. Compartir Información
              </h3>

              <p data-id="element-1700">
                No se compartirá información móvil con terceros ni afiliados con
                fines de marketing ni promocionales. Los datos de suscripción y
                el consentimiento para el uso de mensajes de texto no se
                compartirán con terceros.
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1701">
                4. Seguridad de Datos
              </h3>
              <p data-id="element-1702">
                Implementamos medidas de seguridad técnicas y organizativas
                apropiadas para proteger tu información personal contra acceso
                no autorizado, alteración o destrucción.
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1703">
                5. Tus Derechos
              </h3>
              <p data-id="element-1704">
                Tienes derecho a acceder, corregir o eliminar tu información
                personal. Para ejercer estos derechos, contáctanos en:{' '}
                <a href="mailto:support@truemarketer.com" className="text-[#00f7fc] hover:text-[#00d7dc]" data-id="element-1705">
                  support@truemarketer.com
                </a>
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1706">6. Cookies</h3>
              <p data-id="element-1707">
                Utilizamos cookies y tecnologías similares para mejorar tu
                experiencia en nuestro sitio web y analizar el uso del sitio.
              </p>
              <h3 className="text-lg font-bold text-white mt-6" data-id="element-1708">Contacto</h3>
              <p data-id="element-1709">
                Para preguntas sobre esta política de privacidad, contacta:{' '}
                <a href="mailto:support@truemarketer.com" className="text-[#00f7fc] hover:text-[#00d7dc]" data-id="element-1710">
                  support@truemarketer.com
                </a>
              </p>
            </div>
          </div>
        </div>}
    </div>;
}